// Phase 3B — "Start here": the shortest honest path to a first real experience.
//
// Three steps, in the order the ecosystem is designed to be met:
//   HEAR → READ → PRACTISE
// Each step links to real published content. Nothing is gated, nothing is
// asked for, and the invitation only appears after the loop is closed.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowRight, BookOpen, Check, Play, Wind } from "lucide-react";
import { ACTIVATIONS, RELEASES, WISDOM, getCurrentRelease } from "@/lib/content";
import { rememberPath, recordAction } from "@/lib/path-memory";
import { useReturnLoop } from "@/hooks/use-return-loop";
import { ReturnPrompt } from "@/components/site/ReturnPrompt";
import { NextExperiences } from "@/components/site/NextExperiences";
import { track } from "@/lib/analytics";
import { SITE_URL } from "@/lib/site";
import { seoMeta } from "@/lib/schema";
import type { MemoryKind } from "@/lib/return-loop";

export const Route = createFileRoute("/start")({
  component: StartHere,
  head: () => ({
    ...seoMeta({
      title: "Start Here | Your First Steps Into KAfrequency",
      description:
        "A three-step introduction to KAfrequency by Buddha KA: hear the frequency, read a teaching, then practise a daily activation. No account required.",
      path: "/start",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/start` }],
  }),
});

const DONE_KEY = "ka.start.completed.v1";

interface Step {
  id: string;
  eyebrow: string;
  title: string;
  body: string;
  href: string;
  cta: string;
  icon: typeof Play;
  memory: { kind: MemoryKind; ref: string; title: string; href: string };
}

function buildSteps(): Step[] {
  const release = getCurrentRelease(RELEASES) ?? RELEASES[0]!;
  const teaching = WISDOM[0]!;
  const activation = ACTIVATIONS[0]!;
  return [
    {
      id: "hear",
      eyebrow: "Step one · Hear",
      title: release.title,
      body: release.statement,
      href: `/music/${release.slug}`,
      cta: "Listen now",
      icon: Play,
      memory: {
        kind: "music",
        ref: release.slug,
        title: release.title,
        href: `/music/${release.slug}`,
      },
    },
    {
      id: "read",
      eyebrow: "Step two · Read",
      title: teaching.title,
      body: teaching.excerpt,
      href: `/wisdom/${teaching.slug}`,
      cta: "Read the teaching",
      icon: BookOpen,
      memory: {
        kind: "wisdom",
        ref: teaching.slug,
        title: teaching.title,
        href: `/wisdom/${teaching.slug}`,
      },
    },
    {
      id: "practise",
      eyebrow: "Step three · Practise",
      title: activation.title,
      body: activation.intention,
      href: `/activations/${activation.slug}`,
      cta: "Begin the activation",
      icon: Wind,
      memory: {
        kind: "activation",
        ref: activation.slug,
        title: activation.title,
        href: `/activations/${activation.slug}`,
      },
    },
  ];
}

function StartHere() {
  const steps = buildSteps();
  const loop = useReturnLoop();
  const [done, setDone] = useState<string[]>([]);

  useEffect(() => {
    track("start_here_view");
    try {
      const raw = window.localStorage.getItem(DONE_KEY);
      setDone(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      /* private mode */
    }
  }, []);

  const persist = (next: string[]) => {
    setDone(next);
    try {
      window.localStorage.setItem(DONE_KEY, JSON.stringify(next));
    } catch {
      /* private mode */
    }
    if (next.length === steps.length) track("start_here_complete");
  };

  const markDone = (step: Step) => {
    if (done.includes(step.id)) return;
    rememberPath({ ...step.memory, completed: true });
    recordAction("value");
    track("start_here_step_complete", { step: step.id });
    persist([...done, step.id]);
  };

  const complete = done.length === steps.length;

  return (
    <article className="mx-auto max-w-3xl px-4 py-16 sm:py-24">
      <header>
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Start here</p>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl">Three steps into the frequency</h1>
        <p className="mt-5 max-w-prose text-muted-foreground">
          Hear it, read it, then practise it. Nothing here needs an account, and nothing is asked of
          you until you have received something first.
        </p>
        <p
          className="mt-6 text-sm text-muted-foreground"
          aria-live="polite"
        >
          {done.length} of {steps.length} steps complete
        </p>
      </header>

      <ol className="mt-12 space-y-6">
        {steps.map((step, i) => {
          const Icon = step.icon;
          const isDone = done.includes(step.id);
          return (
            <li
              key={step.id}
              className={`rounded-2xl border hairline p-6 sm:p-8 ${isDone ? "bg-charcoal/20" : "bg-charcoal/40"}`}
            >
              <div className="flex items-start gap-4">
                <span
                  aria-hidden
                  className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border hairline text-gold"
                >
                  {isDone ? <Check size={16} /> : <Icon size={16} />}
                </span>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{step.eyebrow}</p>
                  <h2 className="mt-2 font-serif text-2xl">{step.title}</h2>
                  <p className="mt-3 text-sm text-muted-foreground">{step.body}</p>
                  <div className="mt-6 flex flex-wrap items-center gap-3">
                    <Link
                      to={step.href as never}
                      onClick={() => {
                        rememberPath(step.memory);
                        recordAction("start");
                        track("start_here_step_start", { step: step.id, index: i });
                      }}
                      className="inline-flex min-h-11 items-center gap-2 rounded-full bg-gold-gradient px-6 text-sm font-semibold text-obsidian transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
                    >
                      {step.cta}
                      <ArrowRight size={14} aria-hidden />
                    </Link>
                    <button
                      type="button"
                      onClick={() => markDone(step)}
                      disabled={isDone}
                      className="inline-flex min-h-11 items-center rounded-full border hairline px-5 text-sm text-muted-foreground transition hover:text-foreground disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
                    >
                      {isDone ? "Marked complete" : "I have done this"}
                    </button>
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      {complete && (
        <p className="mt-10 rounded-2xl border hairline bg-charcoal/40 p-6 text-sm text-muted-foreground">
          The loop is closed. Your path is remembered on this device, so the next time you arrive the
          site will offer you exactly where you stopped.
        </p>
      )}

      {loop.ready && <NextExperiences surface="start_here" heading="Where to go from here" />}
      <ReturnPrompt surface="start_here" />
    </article>
  );
}
